"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { ArrowLeft, CheckCircle, AlertCircle, Save, Loader2, Edit2, Plus, Trash2, MessageSquare } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "../../../lib/textron-data-processor";
import type { TailoringQuestion } from "../../shared/BaseTailoringQuestions";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../ui/table";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { SHAREPOINT_FY_YEAR } from "@/constants/sharepoint-config";

interface LitigationsClaimsSubstantiveQuestionsProps {
}

interface QuestionResponse {
  selectedOption?: string;
  textResponse?: { html: string; plainText: string };
  documents?: string[];
  lastUpdated?: string;
  companyName?: string;
}

interface QuestionState {
  selectedOption: string;
  textResponse: { html: string; plainText: string } | null;
  documents: any[];
}

interface LitigationRow {
  subjectMatter: string;
  forum: string;
  amountInvolved: string;
  description: string;
  managementResponse: string;
  responseOfLegalCounsel: string;
}

// Create questions in TailoringQuestion format
function createQuestions(): TailoringQuestion[] {
  const questions: TailoringQuestion[] = [];

  // LIT-03: Document procedures performed
  questions.push({
    id: "LIT-03",
    question: "Document our procedures and confirmations sent to entity's external legal counsel through letter of general inquiry.",
    type: "text",
  });

  // LIT-04: Is it unlikely that external legal counsel will respond appropriately?
  questions.push({
    id: "LIT-04",
    question: "Is it considered unlikely that the entity's external legal counsel will respond appropriately to a letter of general inquiry?",
    type: "radio",
    options: ["Yes", "No"],
    follow_up: {
      id: "LIT-04-doc",
      question: "Document communications through letter of specific inquiry including:\na) A list of litigation and claims;\nb) Where available, management's assessment of the outcome of each of the identified litigation and claims and its estimate of the financial implications, including costs involved; and\nc) A request that the entity's external legal counsel confirm the reasonableness of management's assessments and provide the auditor with further information if the list is considered by the entity's external legal counsel to be incomplete or incorrect.",
      type: "text",
      condition: "Yes",
    },
  });

  // LIT-05: Have we judged it necessary to meet with external legal counsel?
  questions.push({
    id: "LIT-05",
    question: "Have we judged it necessary to meet with the entity's external legal counsel to discuss the likely outcome of the litigation or claims?\n\n(This may be the case, for example, where:\na) The auditor determines that the matter is a significant risk;\nb) The matter is complex.\nc) There is disagreement between management and the entity's external legal counsel.)",
    type: "radio",
    options: ["Yes", "No"],
    follow_up: {
      id: "LIT-05-doc",
      question: "Document meeting with the entity's external legal counsel.",
      type: "text",
      condition: "Yes",
    },
  });

  // LIT-06: Has management refused permission or has legal counsel refused to respond?
  questions.push({
    id: "LIT-06",
    question: "Has the management refused to give us permission to communicate or meet with the entity's external legal counsel, or the entity's external legal counsel has refused to respond appropriately to the letter of inquiry, or is prohibited from responding?",
    type: "radio",
    options: ["Yes", "No"],
    follow_up: {
      id: "LIT-06-alternative",
      question: "Are we able to obtain sufficient appropriate audit evidence by performing alternative audit procedures?",
      type: "radio",
      condition: "Yes",
      options: ["Yes", "No"],
    },
  });

  // Inference and conclusion question (always shown at the end)
  questions.push({
    id: "LIT-INFERENCE",
    question: "Document the inference and conclusion from the above procedure performed.",
    type: "text",
  });

  return questions;
}

export default function LitigationsClaimsSubstantiveQuestions({}: LitigationsClaimsSubstantiveQuestionsProps) {
  const questions = useMemo(() => createQuestions(), []);
  const [companyName, setCompanyName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sharePointSaveStatus, setSharePointSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [questionResponses, setQuestionResponses] = useState<Record<string, QuestionState>>({});
  const [dirtyQuestions, setDirtyQuestions] = useState<Set<string>>(new Set());
  const [litigationsTable, setLitigationsTable] = useState<LitigationRow[]>([]);
  const [isTableDirty, setIsTableDirty] = useState(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Generate question ID for review comments
  const getQuestionIdForReview = (questionId: string): string => {
    return `LITIGATIONS_CLAIMS_${questionId}`;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

        await loadResponses(name);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const loadResponses = async (companyName: string) => {
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const res = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_LIT_Substantive.json");
        if (res.success && res.data) {
          const substantiveProcedures = res.data.substantiveProcedures || {};
          
          const initialAnswers: Record<string, string> = {};
          const initialResponses: Record<string, QuestionState> = {};

          questions.forEach((q) => {
            const response = substantiveProcedures[q.id] as QuestionResponse | undefined;
            if (response) {
              initialAnswers[q.id] = response.selectedOption || "";
              initialResponses[q.id] = {
                selectedOption: response.selectedOption || "",
                textResponse: response.textResponse || null,
                documents: (response.documents || []).map((doc: string) => ({
                  id: `doc_${doc}`,
                  type: 'linked' as const,
                  name: doc
                })),
              };
            } else {
              initialResponses[q.id] = {
                selectedOption: "",
                textResponse: null,
                documents: [],
              };
            }

            // Load follow-up questions
            if (q.follow_up) {
              const followUpResponse = substantiveProcedures[q.follow_up.id] as QuestionResponse | undefined;
              if (followUpResponse) {
                initialAnswers[q.follow_up.id] = followUpResponse.selectedOption || "";
                initialResponses[q.follow_up.id] = {
                  selectedOption: followUpResponse.selectedOption || "",
                  textResponse: followUpResponse.textResponse || null,
                  documents: (followUpResponse.documents || []).map((doc: string) => ({
                    id: `doc_${doc}`,
                    type: 'linked' as const,
                    name: doc
                  })),
                };
              } else {
                initialResponses[q.follow_up.id] = {
                  selectedOption: "",
                  textResponse: null,
                  documents: [],
                };
              }
            }
          });

          // Initialize LIT-06-modify (will show conditionally)
          initialResponses["LIT-06-modify"] = {
            selectedOption: "",
            textResponse: null,
            documents: [],
          };
          
          // Load LIT-06-modify if it exists
          const modifyResponse = substantiveProcedures["LIT-06-modify"] as QuestionResponse | undefined;
          if (modifyResponse) {
            initialAnswers["LIT-06-modify"] = modifyResponse.selectedOption || "";
            initialResponses["LIT-06-modify"] = {
              selectedOption: modifyResponse.selectedOption || "",
              textResponse: null,
              documents: [],
            };
          }

          // Load litigations table
          if (substantiveProcedures["litigations"] && Array.isArray(substantiveProcedures["litigations"])) {
            setLitigationsTable(substantiveProcedures["litigations"] as LitigationRow[]);
          } else {
            setLitigationsTable([]);
          }

          setAnswers(initialAnswers);
          setQuestionResponses(initialResponses);
        }
      }
    } catch (error) {
      console.error("Error loading responses:", error);
    }
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId] || { selectedOption: "", textResponse: null, documents: [] },
        selectedOption: answer
      }
    }));
    setDirtyQuestions(prev => new Set(prev).add(questionId));

    // Initialize LIT-06-modify when LIT-06-alternative is "No"
    if (questionId === "LIT-06-alternative" && answer === "No") {
      setQuestionResponses(prev => ({
        ...prev,
        "LIT-06-modify": prev["LIT-06-modify"] || {
          selectedOption: "",
          textResponse: null,
          documents: [],
        }
      }));
    }
  };

  const handleDocumentsChange = (questionId: string, documents: any[]) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId] || { selectedOption: "", textResponse: null, documents: [] },
        documents: documents.map(doc => doc.name || doc)
      }
    }));
    setDirtyQuestions(prev => new Set(prev).add(questionId));
  };

  const handleTextResponseChange = (questionId: string, textResponse: any) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId] || { selectedOption: "", textResponse: null, documents: [] },
        textResponse
      }
    }));
    setDirtyQuestions(prev => new Set(prev).add(questionId));
  };

  const isQuestionAnswered = (questionId: string) => {
    const hasOptionAnswer =
      answers[questionId] !== undefined && answers[questionId] !== "";

    const response = questionResponses[questionId];
    const hasTextResponse =
      !!response?.textResponse &&
      (
        (response.textResponse.html && response.textResponse.html.trim() !== "") ||
        (response.textResponse.plainText && response.textResponse.plainText.trim() !== "")
      );
    const hasDocuments =
      !!response?.documents && Array.isArray(response.documents) && response.documents.length > 0;
    const hasTable =
      !!response?.table && Array.isArray(response.table) && response.table.length > 0;

    return hasOptionAnswer || hasTextResponse || hasDocuments || hasTable;
  };

  const shouldShowFollowUp = (question: TailoringQuestion) => {
    if (!question.follow_up) return false;
    if (question.follow_up.condition === "always") return true;
    return answers[question.id] === question.follow_up.condition;
  };

  // Helper function to sanitize data for JSON serialization
  const sanitizeForJSON = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (typeof obj !== 'object') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForJSON(item));
    }
    
    // Handle Date objects
    if (obj instanceof Date) {
      return obj.toISOString();
    }
    
    // Create a new object with only serializable properties
    const sanitized: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        // Skip functions, symbols, and other non-serializable types
        if (typeof value === 'function' || typeof value === 'symbol') {
          continue;
        }
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeForJSON(value);
      }
    }
    return sanitized;
  };

  const saveResponses = useCallback(async () => {
    if (!companyName) return;

    try {
      // Trigger auto-save for all DocumentManager instances
      for (const question of questions) {
        if (question.type === "text" || question.follow_up) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_ConcludingProcedures_LIT_${question.id}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
          if (question.follow_up) {
            const followUpAutoSave = (window as any)[`autoSaveTextbox_ConcludingProcedures_LIT_${question.follow_up.id}`];
            if (followUpAutoSave && typeof followUpAutoSave === 'function') {
              followUpAutoSave();
            }
          }
        }
      }
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSharePointSaveStatus('saving');
      setSaveError(null);

      // Load existing file
      let existingData: any = {};
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const res = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_LIT_Substantive.json");
        if (res.success && res.data) {
          existingData = res.data;
        }
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      const substantiveProcedures: Record<string, QuestionResponse> = {};

      // Save all questions
      for (const q of questions) {
        const state = questionResponses[q.id] || { selectedOption: "", textResponse: null, documents: [] };
        const textboxFromLocalStorage = localStorage.getItem(`textbox_ConcludingProcedures_LIT_${q.id}`);
        const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;

        // For inference question, query db.json for section field like CustomProcedure
        let documentNames: string[] = [];
        if (q.id === "LIT-INFERENCE") {
          const sectionToFind = `ConcludingProcedures_LIT_${q.id}`;
          try {
            if ((window as any).sharePointAPI?.loadDocumentListBySection) {
              const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
              if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
                documentNames = dbResult.data
                  .map((file: any) => file.name || '')
                  .filter((name: string) => name && name.trim().length > 0);
              }
            }
          } catch (error) {
            console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
          }
        } else {
          const fileDocuments = (state.documents || []).filter((d: any) => d.type !== 'textbox');
          documentNames = fileDocuments.map((d: any) => {
            // Ensure we only extract string names, not objects
            if (typeof d === 'string') return d;
            if (d && typeof d === 'object' && d.name) return d.name;
            return String(d);
          });
        }

        let textResponse = { html: "", plainText: "" };
        if (hasTextboxContent) {
          textResponse = {
            html: textboxFromLocalStorage,
            plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, '')
          };
        } else if (state.textResponse) {
          textResponse = state.textResponse;
        }

        substantiveProcedures[q.id] = {
          selectedOption: state.selectedOption || "",
          textResponse: textResponse,
          documents: documentNames,
          lastUpdated: new Date().toISOString(),
          companyName: companyName,
        };

        // Save follow-up questions
        if (q.follow_up && shouldShowFollowUp(q)) {
          const followUpState = questionResponses[q.follow_up.id] || { selectedOption: "", textResponse: null, documents: [] };
          const followUpTextbox = localStorage.getItem(`textbox_ConcludingProcedures_LIT_${q.follow_up.id}`);
          const hasFollowUpTextbox = followUpTextbox && followUpTextbox.trim().length > 0;

          const followUpFileDocuments = (followUpState.documents || []).filter((d: any) => d.type !== 'textbox');
          const followUpDocumentNames = followUpFileDocuments.map((d: any) => {
            // Ensure we only extract string names, not objects
            if (typeof d === 'string') return d;
            if (d && typeof d === 'object' && d.name) return d.name;
            return String(d);
          });

          let followUpTextResponse = { html: "", plainText: "" };
          if (hasFollowUpTextbox) {
            followUpTextResponse = {
              html: followUpTextbox,
              plainText: followUpTextbox.replace(/<[^>]*>/g, '')
            };
          } else if (followUpState.textResponse) {
            followUpTextResponse = followUpState.textResponse;
          }

          substantiveProcedures[q.follow_up.id] = {
            selectedOption: followUpState.selectedOption || "",
            textResponse: followUpTextResponse,
            documents: followUpDocumentNames,
            lastUpdated: new Date().toISOString(),
            companyName: companyName,
          };
        }
      }

      // Save LIT-06-modify if LIT-06-alternative is "No" and it's been answered
      if (questionResponses["LIT-06-alternative"]?.selectedOption === "No") {
        const modifyState = questionResponses["LIT-06-modify"];
        if (modifyState && (modifyState.selectedOption || dirtyQuestions.has("LIT-06-modify"))) {
          substantiveProcedures["LIT-06-modify"] = {
            selectedOption: modifyState.selectedOption || "",
            textResponse: null,
            documents: [],
            lastUpdated: new Date().toISOString(),
            companyName: companyName,
          };
        }
      }

      // Save litigations table if it's been modified
      if (isTableDirty || litigationsTable.length > 0) {
        existingData.substantiveProcedures["litigations"] = litigationsTable;
      }

      // Merge into existing data
      Object.entries(substantiveProcedures).forEach(([qId, value]) => {
        existingData.substantiveProcedures[qId] = value;
      });

      // Sanitize the data before stringifying to remove any non-serializable properties
      const sanitizedData = sanitizeForJSON(existingData);
      const jsonString = JSON.stringify(sanitizedData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: "ConcludingProcedures_LIT_Substantive.json",
        referenceValue: "Concluding Procedures Litigations & Claims Substantive Responses",
        folderName: "juggernaut",
        fyYear: String(SHAREPOINT_FY_YEAR),
        sectionList: ["ConcludingProcedures_LIT_Substantive"],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setSharePointSaveStatus('saved');
      setDirtyQuestions(new Set());
      setIsTableDirty(false);
      setIsEditMode(false);
    } catch (error: any) {
      console.error("Error saving responses:", error);
      setSharePointSaveStatus('error');
      setSaveError(error?.message || "Failed to save responses");
    }
  }, [companyName, questions, answers, questionResponses, litigationsTable, isTableDirty, dirtyQuestions]);

  const handleComplete = async () => {
    if (isEditMode) {
      try {
        setIsSaving(true);
        await saveResponses();
      } catch (error) {
        console.error("Error completing:", error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const renderQuestion = (question: TailoringQuestion, isFollowUp = false) => {
    const isAnswered = isQuestionAnswered(question.id);
    
    return (
      <Card key={question.id} className={`border-white/10 bg-white/5 text-white ${isFollowUp ? 'ml-6 border-l-2 border-l-[#4da3ff]/30' : ''}`}>
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                {isAnswered ? (
                  <CheckCircle className="h-5 w-5 text-green-400" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-white/40" />
                )}
              </div>
              <div className="flex-1">
                <Label className="text-sm font-medium text-white whitespace-pre-line">
                  {question.question}
                </Label>
              </div>
            </div>

            {question.type === "radio" && question.options && (
              <RadioGroup
                value={answers[question.id] || ""}
                onValueChange={(value) => handleAnswerChange(question.id, value)}
                disabled={!isEditMode}
                className="ml-8"
              >
                {question.options.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value={option} 
                      id={`${question.id}-${option}`}
                      className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                    />
                    <Label 
                      htmlFor={`${question.id}-${option}`}
                      className="text-sm text-white/80 cursor-pointer whitespace-pre-line"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}

            {(question.type === "document" || question.type === "text") && (
              <div className="ml-8 mt-3">
                <DocumentManager
                  sectionKey="concluding-lit"
                  itemId={question.id}
                  itemQuestion={question.question}
                  itemTextResponse={questionResponses[question.id]?.textResponse}
                  itemDocuments={questionResponses[question.id]?.documents?.map((doc: string | any) => {
                    if (typeof doc === 'string') {
                      return {
                        id: `doc_${doc}`,
                        type: 'linked' as const,
                        name: doc
                      };
                    }
                    return doc;
                  }) || []}
                  onDocumentsChange={(docs) => handleDocumentsChange(question.id, docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange(question.id, textResp)}
                  disabled={!isEditMode}
                />
              </div>
            )}

            {/* Show DocumentManager inline for radio questions with "always" condition follow-up */}
            {question.type === "radio" && question.follow_up && question.follow_up.condition === "always" && (question.follow_up.type === "text" || question.follow_up.type === "document") && (
              <div className="ml-8 mt-3">
                <DocumentManager
                  sectionKey="concluding-lit"
                  itemId={question.follow_up.id}
                  itemQuestion={question.follow_up.question}
                  itemTextResponse={questionResponses[question.follow_up.id]?.textResponse}
                  itemDocuments={questionResponses[question.follow_up.id]?.documents?.map((doc: string | any) => {
                    if (typeof doc === 'string') {
                      return {
                        id: `doc_${doc}`,
                        type: 'linked' as const,
                        name: doc
                      };
                    }
                    return doc;
                  }) || []}
                  onDocumentsChange={(docs) => handleDocumentsChange(question.follow_up!.id, docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange(question.follow_up!.id, textResp)}
                  disabled={!isEditMode}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Assessment</h2>
            <p className="text-sm text-white/60">Loading questions...</p>
          </div>
        </div>
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4da3ff] mx-auto mb-4"></div>
              <p>Loading questions...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Assessment</h2>
            <p className="text-sm text-white/60">Answer the following questions for assessment procedures.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditMode(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleComplete}
                disabled={isSaving}
                className="bg-[#4da3ff] hover:bg-[#4da3ff]/80"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setIsEditMode(true)}>
              <Edit2 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {saveError && (
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-300">
              <AlertCircle className="h-5 w-5" />
              <p>{saveError}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {sharePointSaveStatus === 'saved' && (
        <Card className="border-green-500/20 bg-green-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-300">
              <CheckCircle className="h-5 w-5" />
              <p>Responses saved successfully</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Litigations Table */}
      <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-lg">Litigations</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getQuestionIdForReview("LITIGATIONS"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="rounded-md border border-white/10 bg-black/20 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-white/5">
                    <TableHead className="text-white/80 font-medium">Subject Matter</TableHead>
                    <TableHead className="text-white/80 font-medium">Forum</TableHead>
                    <TableHead className="text-white/80 font-medium">Amount Involved</TableHead>
                    <TableHead className="text-white/80 font-medium">Description</TableHead>
                    <TableHead className="text-white/80 font-medium">Management Response</TableHead>
                    <TableHead className="text-white/80 font-medium">Response of Legal Counsel</TableHead>
                    <TableHead className="w-[100px] text-white/80">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {litigationsTable.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-8 text-center text-white/60"
                      >
                        No rows added. Click "Add Row" to add a new entry.
                      </TableCell>
                    </TableRow>
                  ) : (
                    litigationsTable.map((row, index) => (
                      <TableRow key={index} className="border-white/10 hover:bg-white/5">
                        <TableCell>
                          <Input
                            value={row.subjectMatter || ""}
                            onChange={(e) => {
                              const updated = [...litigationsTable];
                              updated[index] = { ...updated[index], subjectMatter: e.target.value };
                              setLitigationsTable(updated);
                              setIsTableDirty(true);
                            }}
                            disabled={!isEditMode}
                            className="text-white border-white/10 bg-black/20"
                            placeholder="Enter subject matter"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.forum || ""}
                            onChange={(e) => {
                              const updated = [...litigationsTable];
                              updated[index] = { ...updated[index], forum: e.target.value };
                              setLitigationsTable(updated);
                              setIsTableDirty(true);
                            }}
                            disabled={!isEditMode}
                            className="text-white border-white/10 bg-black/20"
                            placeholder="Enter forum"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.amountInvolved || ""}
                            onChange={(e) => {
                              const updated = [...litigationsTable];
                              updated[index] = { ...updated[index], amountInvolved: e.target.value };
                              setLitigationsTable(updated);
                              setIsTableDirty(true);
                            }}
                            disabled={!isEditMode}
                            className="text-white border-white/10 bg-black/20"
                            placeholder="Enter amount"
                          />
                        </TableCell>
                        <TableCell>
                          <Textarea
                            value={row.description || ""}
                            onChange={(e) => {
                              const updated = [...litigationsTable];
                              updated[index] = { ...updated[index], description: e.target.value };
                              setLitigationsTable(updated);
                              setIsTableDirty(true);
                            }}
                            disabled={!isEditMode}
                            className="text-white border-white/10 bg-black/20 min-h-[60px]"
                            placeholder="Enter description"
                          />
                        </TableCell>
                        <TableCell>
                          <Textarea
                            value={row.managementResponse || ""}
                            onChange={(e) => {
                              const updated = [...litigationsTable];
                              updated[index] = { ...updated[index], managementResponse: e.target.value };
                              setLitigationsTable(updated);
                              setIsTableDirty(true);
                            }}
                            disabled={!isEditMode}
                            className="text-white border-white/10 bg-black/20 min-h-[60px]"
                            placeholder="Enter management response"
                          />
                        </TableCell>
                        <TableCell>
                          <Textarea
                            value={row.responseOfLegalCounsel || ""}
                            onChange={(e) => {
                              const updated = [...litigationsTable];
                              updated[index] = { ...updated[index], responseOfLegalCounsel: e.target.value };
                              setLitigationsTable(updated);
                              setIsTableDirty(true);
                            }}
                            disabled={!isEditMode}
                            className="text-white border-white/10 bg-black/20 min-h-[60px]"
                            placeholder="Enter legal counsel response"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const updated = litigationsTable.filter((_, i) => i !== index);
                              setLitigationsTable(updated);
                              setIsTableDirty(true);
                            }}
                            disabled={!isEditMode}
                            className="text-white/80 hover:text-white border-white/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                const newRow: LitigationRow = {
                  subjectMatter: "",
                  forum: "",
                  amountInvolved: "",
                  description: "",
                  managementResponse: "",
                  responseOfLegalCounsel: "",
                };
                setLitigationsTable([...litigationsTable, newRow]);
                setIsTableDirty(true);
              }}
              disabled={!isEditMode}
              className="text-white/80 hover:text-white border-white/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {questions.map((question) => (
          <div key={question.id} className="space-y-3">
            {renderQuestion(question)}
            {shouldShowFollowUp(question) && question.follow_up && question.follow_up.condition !== "always" && (
              <div className="transition-all duration-300">
                {renderQuestion(question.follow_up, true)}
              </div>
            )}
            {/* Handle LIT-06-modify separately - show when LIT-06-alternative is "No" */}
            {question.id === "LIT-06" && questionResponses["LIT-06-alternative"]?.selectedOption === "No" && (
              <div className="transition-all duration-300 ml-6 border-l-2 border-l-[#4da3ff]/30 pl-4">
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <AlertCircle className="h-5 w-5 text-white/40" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-sm font-medium text-white whitespace-pre-line">
                            If you answered 'No' to 'Are we able to obtain sufficient appropriate audit evidence by performing alternative audit procedures?', then: Modify the opinion in the auditor's report in accordance with SA 705.
                          </Label>
                        </div>
                      </div>
                      <RadioGroup
                        value={questionResponses["LIT-06-modify"]?.selectedOption || ""}
                        onValueChange={(value) => handleAnswerChange("LIT-06-modify", value)}
                        disabled={!isEditMode}
                        className="ml-8"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value="Yes" 
                            id="LIT-06-modify-Yes"
                            className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                          />
                          <Label 
                            htmlFor="LIT-06-modify-Yes"
                            className="text-sm text-white/80 cursor-pointer"
                          >
                            Yes
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value="No" 
                            id="LIT-06-modify-No"
                            className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                          />
                          <Label 
                            htmlFor="LIT-06-modify-No"
                            className="text-sm text-white/80 cursor-pointer"
                          >
                            No
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

